import React, { useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Select, Checkbox, Button } from '@crisellevaldez/speculo-ui';
import { Plus, X } from 'lucide-react';
import { isValidAreaCode, twilioSetupSchema, TwilioSetupData } from '../schemas/onboarding.schema';
import { US_STATES } from '../../../../constants/states';
import { HttpsUrlInput } from '../../../common/HttpsUrlInput';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { SectionHeading } from '../components/SectionHeading';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { InfoNotice } from '../components/InfoNotice';
import { useAddressAutofill } from '../../../../hooks/address/useAddressAutofill';

interface TwilioSetupStepProps {
  onNext: (data: TwilioSetupData) => void;
  onBack: () => void;
  initialData?: Partial<TwilioSetupData>;
  isLoading?: boolean;
  officeAddress?: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: 'US' | 'Canada';
  };
}

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'Canada', label: 'Canada' },
];

const BUSINESS_TYPE_OPTIONS = [
  { value: 'LLC', label: 'Limited Liability Company' },
  { value: 'Corporation', label: 'Corporation' },
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
];

export const TwilioSetupStep: React.FC<TwilioSetupStepProps> = ({
  onNext,
  onBack,
  initialData,
  isLoading = false,
  officeAddress,
}) => {
  const [newAreaCode, setNewAreaCode] = useState('');
  const [newAreaCodeError, setNewAreaCodeError] = useState<string | null>(null);
  const otherAreaCodesRef = useRef<HTMLDivElement>(null);
  const acknowledgementRef = useRef<HTMLDivElement>(null);

  const form = useForm<TwilioSetupData>({
    resolver: zodResolver(twilioSetupSchema),
    defaultValues: {
      preferred_area_code: '',
      preferred_location: '',
      other_area_codes: [],
      area_code_acknowledgement: false,
      legal_business_name: '',
      business_type: 'LLC',
      ein_cbn: '',
      business_website: '',
      ...initialData,
      business_address: {
        street: initialData?.business_address?.street || officeAddress?.street || '',
        city: initialData?.business_address?.city || officeAddress?.city || '',
        state: initialData?.business_address?.state || officeAddress?.state || '',
        zip: initialData?.business_address?.zip || officeAddress?.zip || '',
        country: initialData?.business_address?.country || officeAddress?.country || 'US',
      },
    },
  });

  const { handleZipCodeChange, isLookingUp } = useAddressAutofill({
    setValue: (name: any, value: any) => {
      // Map client_address fields to business_address fields
      const mappedName = name.replace('client_address', 'business_address');
      form.setValue(mappedName as any, value);
    },
    getValues: () => {
      const businessAddress = form.getValues('business_address');
      return {
        client_address: businessAddress,
      } as any;
    },
  });

  const handleSubmit = async (data: TwilioSetupData) => {
    // Don't send webhook here - will send after entire Stage 3 is complete in parent
    onNext(data);
  };

  const handleAddAreaCode = () => {
    const code = newAreaCode.trim();
    if (!isValidAreaCode(code)) {
      setNewAreaCodeError('Area codes must be 3 digits');
      return;
    }
    const current = form.getValues('other_area_codes') || [];
    if (current.includes(code)) {
      setNewAreaCodeError('This area code is already added');
      return;
    }
    form.setValue('other_area_codes', [...current, code], { shouldValidate: true });
    setNewAreaCode('');
    setNewAreaCodeError(null);
  };

  const handleInvalid = (errors: any) => {
    if (errors.other_area_codes && otherAreaCodesRef.current) {
      otherAreaCodesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (errors.area_code_acknowledgement && acknowledgementRef.current) {
      acknowledgementRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const otherAreaCodesError =
    newAreaCodeError ?? form.formState.errors.other_area_codes?.message ?? undefined;

  return (
    <StageContainer
      title="Phone Number & Business Verification"
      description="We'll set up your dedicated phone number and verify your business information for compliance."
    >
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="space-y-8"
        noValidate
      >
        {/* Phone Number Preferences Section */}
        <div className="space-y-6">
          <SectionHeading title="Phone Number Preferences" />

          <FormSection>
            <Form.Control required>
              <QuestionLabel>What is your preferred local area code?</QuestionLabel>
              <Input
                {...form.register('preferred_area_code')}
                placeholder="e.g., 415"
                maxLength={3}
                error={form.formState.errors.preferred_area_code?.message}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>What is your preferred location?</QuestionLabel>
              <Input
                {...form.register('preferred_location')}
                placeholder="e.g., San Francisco, CA"
                error={form.formState.errors.preferred_location?.message}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <div ref={otherAreaCodesRef}>
              <Form.Control required>
                <QuestionLabel>Other acceptable area codes?</QuestionLabel>
                <div className="flex gap-2">
                  <Input
                    value={newAreaCode}
                    onChange={(e) => {
                      setNewAreaCode(e.target.value);
                      setNewAreaCodeError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAreaCode();
                      }
                    }}
                    placeholder="e.g., 408"
                    maxLength={3}
                    inputMode="numeric"
                    error={otherAreaCodesError}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddAreaCode}
                    disabled={!newAreaCode.trim()}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </Button>
                </div>
              </Form.Control>
            </div>

            {(form.watch('other_area_codes') || []).length > 0 && (
              <>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(form.watch('other_area_codes') || []).map((code, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-700">{code}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const current = form.getValues('other_area_codes') || [];
                          form.setValue(
                            'other_area_codes',
                            current.filter((_, i) => i !== index),
                            { shouldValidate: form.formState.isSubmitted }
                          );
                        }}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-4" ref={acknowledgementRef}>
                  <Controller
                    control={form.control}
                    name="area_code_acknowledgement"
                    render={({ field: { value, onChange } }) => (
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          {/* Label rendered separately so the box aligns with the first
                              line of text instead of the vertical center. */}
                          <Checkbox
                            id="area_code_acknowledgement"
                            className="h-5 w-5"
                            checked={value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                          <label
                            htmlFor="area_code_acknowledgement"
                            className="cursor-pointer text-sm font-medium text-foreground/85"
                          >
                            I understand that other acceptable area codes or locations may be used
                            for Remi's phone number if my preferred choice is unavailable.{' '}
                            <span className="text-red-600 dark:text-red-400">*</span>
                          </label>
                        </div>
                        {form.formState.errors.area_code_acknowledgement && (
                          <p className="text-sm text-red-600">
                            {form.formState.errors.area_code_acknowledgement.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </>
            )}
          </FormSection>
        </div>

        {/* Business Verification Section */}
        <div className="space-y-6">
          <SectionHeading title="Business Verification" />

          <InfoNotice>
            This information is required for Twilio compliance. We'll handle the verification
            process on your behalf once submitted.
          </InfoNotice>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Legal business name</QuestionLabel>
              <Input
                {...form.register('legal_business_name')}
                placeholder="As registered with Secretary of State"
                error={form.formState.errors.legal_business_name?.message}
              />
              <TipBox>
                Name of the company (LLC, Corporation) as officially registered with the Secretary
                of State
              </TipBox>
            </Form.Control>
          </FormSection>

          <FormSection>
            <QuestionLabel>Business address as registered</QuestionLabel>

            <div className="space-y-4">
              <Form.Control required>
                <Form.Label>Street Address</Form.Label>
                <Input
                  {...form.register('business_address.street')}
                  placeholder="123 Main Street"
                  error={form.formState.errors.business_address?.street?.message}
                />
              </Form.Control>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Control required>
                  <Form.Label>ZIP Code</Form.Label>
                  <Input
                    {...form.register('business_address.zip')}
                    placeholder="94102"
                    error={form.formState.errors.business_address?.zip?.message}
                    onChange={(e) => {
                      form.setValue('business_address.zip', e.target.value);
                      handleZipCodeChange(e.target.value);
                    }}
                    disabled={isLookingUp}
                  />
                  {isLookingUp && (
                    <p className="text-sm text-gray-500 mt-1">Looking up location...</p>
                  )}
                </Form.Control>

                <Form.Control required>
                  <Form.Label>City</Form.Label>
                  <Input
                    {...form.register('business_address.city')}
                    placeholder="San Francisco"
                    error={form.formState.errors.business_address?.city?.message}
                  />
                </Form.Control>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Form.Control required>
                  <Form.Label>State</Form.Label>
                  <Controller
                    control={form.control}
                    name="business_address.state"
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={US_STATES}
                        placeholder="Select state"
                        error={form.formState.errors.business_address?.state?.message}
                        searchable
                      />
                    )}
                  />
                </Form.Control>

                <Form.Control required>
                  <Form.Label>Country</Form.Label>
                  <Controller
                    control={form.control}
                    name="business_address.country"
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={COUNTRY_OPTIONS}
                        placeholder="Select country"
                        error={form.formState.errors.business_address?.country?.message}
                      />
                    )}
                  />
                </Form.Control>
              </div>
            </div>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Business type</QuestionLabel>
              <Controller
                control={form.control}
                name="business_type"
                render={({ field }) => (
                  <Select
                    {...field}
                    options={BUSINESS_TYPE_OPTIONS}
                    placeholder="Select business type"
                    error={form.formState.errors.business_type?.message}
                  />
                )}
              />
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>EIN / CBN</QuestionLabel>
              <Input
                {...form.register('ein_cbn')}
                placeholder="12-3456789"
                error={form.formState.errors.ein_cbn?.message}
              />
              <TipBox>Employer Identification Number or Canadian Business Number</TipBox>
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Business website URL</QuestionLabel>
              <HttpsUrlInput
                {...form.register('business_website')}
                placeholder="yourcompany.com"
                error={form.formState.errors.business_website?.message}
              />
            </Form.Control>
          </FormSection>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center gap-3">
          <NextButton type="button" onClick={onBack} variant="secondary">
            Back to Remi Setup
          </NextButton>
          <NextButton isLoading={isLoading} />
        </div>
      </form>
    </StageContainer>
  );
};
